import { useState, useCallback, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "./ZoneManager.module.css";
import AddEditZonesModal from "../../../components/GeoData/Zones/AddEditZonesModal";
import ToggleSwitch from "../../../components/BaseComponents/Toggle/ToggleSwitch";
import DeleteConfirmModal from "../../../components/BaseComponents/DeleteConfirmModal/DeleteConfirmModal";
import DestinationSelect from "../../../components/GeoData/common/DestinationSelect";
import { useToast } from "../../../context/ToastContext";
import {
  useGetZones,
  useCreateZone,
  useUpdateZone,
  useDeleteZone,
} from "../../../api/geo/useZones";
import { isBlankZoneName, zoneDisplayName } from "./zoneName";
import { parentOff } from "../../../utils/geoParentOff";
import { geoCascadeMessage } from "../../../utils/geoCascadeMessage";

/** Map API zone to row shape (code = zoneCode, marketing from title/paragraphs) */
const toRow = (z) => ({
  ...z,
  code: z.zoneCode,
  country: z.countryName,
  marketing: {
    title: z.title ?? "",
    para1: z.paragraph1 ?? "",
    para2: z.paragraph2 ?? "",
  },
});

// Pending toggle changes: { [rowId]: { active? } }
const getDisplayValue = (row, field, pendingChanges) =>
  pendingChanges[row.id]?.[field] ?? row[field];

const ZoneManager = () => {
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [destinationFilter, setDestinationFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");

  // Sync filters from URL query params (e.g. when navigating from Countries or Cities page)
  useEffect(() => {
    const destinationId = searchParams.get("destinationId") || "";
    const countryId = searchParams.get("countryId") || "";
    setDestinationFilter(destinationId);
    setCountryFilter(countryId);
    if (destinationId || countryId) setPage(1);
  }, [searchParams]);
  const [showAddEdit, setShowAddEdit] = useState(false);
  const [mode, setMode] = useState("add");
  const [selectedRow, setSelectedRow] = useState(null);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [pendingChanges, setPendingChanges] = useState({});
  const [toggleSaveLoading, setToggleSaveLoading] = useState(false);

  const { data: zonesData, pagination, loading, error, refetch } = useGetZones({
    page,
    limit,
    search: search || undefined,
    countryId: countryFilter || undefined,
    destinationId: destinationFilter || undefined,
    sortBy: "name",
    sortOrder: "ASC",
  });

  const { execute: createZone, loading: createLoading } = useCreateZone();
  const { execute: updateZone, loading: updateLoading } = useUpdateZone();
  const { execute: deleteZone, loading: deleteLoading } = useDeleteZone();

  const rows = (zonesData || []).map(toRow);
  const saveLoading = createLoading || updateLoading;
  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  const pageNumbers = useMemo(() => {
    const total = pagination?.totalPages ?? 1;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const current = page;
    const pages = new Set([1, total]);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) pages.add(i);
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    let prev = 0;
    for (const p of sorted) {
      if (p > prev + 1) result.push("…");
      result.push(p);
      prev = p;
    }
    return result;
  }, [pagination?.totalPages, page]);

  const applyToggle = useCallback((row, field) => {
    setPendingChanges((prev) => {
      const current = prev[row.id] ?? {};
      const serverVal = row[field];
      const pendingVal = current[field];
      const nextVal = pendingVal !== undefined ? !pendingVal : !serverVal;
      return {
        ...prev,
        [row.id]: { ...current, [field]: nextVal },
      };
    });
  }, []);

  // Auto-search: debounce the input so the list filters as you type (no button).
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
      setPendingChanges({});
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleAdd = () => {
    setMode("add");
    setSelectedRow(null);
    setShowAddEdit(true);
  };

  const handleEdit = (row) => {
    setMode("edit");
    setSelectedRow(row);
    setShowAddEdit(true);
  };

  const handleDeleteClick = (row) => setRowToDelete(row);

  const handleConfirmDelete = async () => {
    if (!rowToDelete) return;
    try {
      await deleteZone(rowToDelete.id);
      setPendingChanges((prev) => {
        const next = { ...prev };
        delete next[rowToDelete.id];
        return next;
      });
      refetch();
      setRowToDelete(null);
      showToast("Zone deleted successfully", "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to delete zone", "error");
    }
  };

  const handleSaveToggles = async () => {
    const ids = Object.keys(pendingChanges);
    if (ids.length === 0) return;
    setToggleSaveLoading(true);
    try {
      // Switching a zone off closes every hotel in it, so tally what the API reports and say
      // so — silently deactivating hotels would be a nasty surprise.
      let switchedOff = 0;
      let restored = 0;
      for (const id of ids) {
        const patch = pendingChanges[id];
        const row = rows.find((r) => String(r.id) === String(id));
        const payload = { active: patch.active ?? row?.active };
        const res = await updateZone(id, payload);
        switchedOff += res?.hotelCascade?.switchedOff || 0;
        restored += res?.hotelCascade?.restored || 0;
      }
      setPendingChanges({});
      refetch();
      showToast(geoCascadeMessage({ switchedOff, restored }), "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to save changes", "error");
    } finally {
      setToggleSaveLoading(false);
    }
  };

  const handleSave = async (payload) => {
    try {
      if (mode === "add") {
        await createZone(payload);
        showToast("Zone created successfully", "success");
      } else if (selectedRow?.id) {
        await updateZone(selectedRow.id, payload);
        showToast("Zone updated successfully", "success");
      }
      setShowAddEdit(false);
      refetch();
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to save zone";
      showToast(msg, "error");
      throw new Error(msg);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.manager}>Zones</div>
        <button className={styles.add} onClick={handleAdd}>
          + Add Zone
        </button>
      </div>

      <div className={styles.tagLine}>
        <span className={styles.tagIcon} aria-hidden>ℹ</span>
        <span>Zones define areas within cities or destinations. Used for location grouping and search refinement.</span>
      </div>

      <div className={styles.filterBar}>
        <div className={styles.searchForm}>
          <input
            type="text"
            placeholder="Search zone or code..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        {/* Type-to-search city filter. The old fixed dropdown pre-loaded only the first 500
            cities alphabetically, so it never reached past "B"; this queries the API as you
            type, so every city is reachable. */}
        <DestinationSelect
          value={destinationFilter}
          allowAll
          allLabel="All Cities"
          placeholder="City: All"
          width="240px"
          onChange={(e) => {
            setDestinationFilter(e.target.value);
            setPage(1);
            setPendingChanges({});
          }}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading zones…</div>
      ) : (
        <>
          <div className={styles.resultsSummary}>
            Showing {rows.length} of {pagination?.total ?? 0} zones
          </div>
          <div className={styles.tableSection}>
            <div className={styles.tableCard}>
              <table className={styles.table}>
              <thead>
                <tr>
                  <th>Zone Name</th>
                  <th>Zone Code</th>
                  <th>City / Destination</th>
                  <th>Country</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className={styles.noResults}>
                      No zones found.
                    </td>
                  </tr>
                ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      {isBlankZoneName(row.name) ? (
                        <span className={styles.unnamedZone} title="This zone has no name in the source data">
                          {zoneDisplayName(row)}
                        </span>
                      ) : (
                        row.name
                      )}
                    </td>
                    <td className={styles.code}>{row.zoneCode ?? row.code}</td>
                    <td>{row.destinationName}</td>
                    <td>{row.countryName ?? row.country}</td>
                    <td>
                      {parentOff(row) ? (
                        <>
                          {/* Off through its parent: shown off, own setting kept (applies again when the parent is ON). */}
                          <ToggleSwitch checked={false} disabled onChange={() => {}} />
                          <div style={{ fontSize: 11, color: "#8a8f98", marginTop: 4, whiteSpace: "nowrap" }}>
                            Off – {parentOff(row)} is off (own: {getDisplayValue(row, "active", pendingChanges) ? "ON" : "OFF"})
                          </div>
                        </>
                      ) : (
                        <ToggleSwitch
                          checked={!!getDisplayValue(row, "active", pendingChanges)}
                          onChange={() => applyToggle(row, "active")}
                        />
                      )}
                    </td>
                    <td className={styles.rowActions}>
                      <button onClick={() => handleEdit(row)}>Edit</button>
                      <button onClick={() => handleDeleteClick(row)}>Delete</button>
                    </td>
                  </tr>
                )))}
              </tbody>
              </table>
            </div>

            <div className={styles.footerBar}>
              <div className={styles.pagination}>
                <span className={styles.paginationInfo}>
                  Page {pagination?.page ?? page} of {pagination?.totalPages || 1} ({pagination?.total ?? 0} total)
                </span>
                {(pagination?.totalPages ?? 1) > 1 && (
                  <div className={styles.pageNumbers}>
                    {pageNumbers.map((p, idx) =>
                      p === "…" ? (
                        <span key={`ellipsis-${idx}`} className={styles.ellipsis}>…</span>
                      ) : (
                        <button
                          key={p}
                          className={`${styles.pageBtn} ${Number(p) === Number(page) ? styles.pageBtnActive : ""}`}
                          onClick={() => {
                            setPage(p);
                            setPendingChanges({});
                          }}
                        >
                          {p}
                        </button>
                      )
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                className={styles.saveChangesBtn}
                onClick={handleSaveToggles}
                disabled={!hasPendingChanges || saveLoading}
              >
                {toggleSaveLoading ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </>
      )}

      <AddEditZonesModal
        open={showAddEdit}
        onClose={() => setShowAddEdit(false)}
        mode={mode}
        initialData={selectedRow}
        onSave={handleSave}
        saveLoading={saveLoading}
      />

      <DeleteConfirmModal
        open={!!rowToDelete}
        onClose={() => setRowToDelete(null)}
        title="Delete zone"
        itemName={rowToDelete?.name}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
      />
    </div>
  );
};

export default ZoneManager;
